import { Mail, MapPin, MapPinned, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthShell from "../components/AuthShell";
import biharData from "../data/bihar_register_final_cleaned.json";
import api, { states } from "../services/api";

const Field = ({
  icon: Icon,
  label,
  name,
  value,
  onChange,
  error,
  type = "text",
  ...rest
}) => (
  <label className="block space-y-2">
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div
      className={`group flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 transition ${
        error ? "border-rose-300" : "border-slate-200 focus-within:border-orange-400"
      }`}
    >
      <Icon className="h-4 w-4 text-slate-400 transition group-focus-within:text-orange-500" />
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        {...rest}
      />
    </div>
    {error && <p className="text-sm text-rose-500">{error}</p>}
  </label>
);

const RegisterDetails = () => {
  const navigate = useNavigate();
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedStateData, setSelectedStateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    fullName: "",
    email: localStorage.getItem("pendingRegisterEmail") || "",
    state: "Bihar",
    district: "",
    mlaConstituency: "",
    mpConstituency: "",
    streetAddress: "",
    pincode: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!localStorage.getItem("verifiedPhone")) {
      toast.error("Please verify your phone number first.");
      navigate("/register");
    }
  }, [navigate]);

  useEffect(() => {
    states
      .getAll()
      .then((response) => setAvailableStates(response.data || []))
      .catch(() => setAvailableStates([]));
  }, []);

  useEffect(() => {
    states
      .getByName(formData.state)
      .then((response) => setSelectedStateData(response.data))
      .catch(() => setSelectedStateData(null));
  }, [formData.state]);

  const districtOptions = useMemo(() => {
    if (formData.state === "Bihar") {
      return biharData.districts;
    }

    return [
      ...new Set(
        (selectedStateData?.mlas || [])
          .map((mla) => mla.district)
          .filter(Boolean),
      ),
    ].sort();
  }, [formData.state, selectedStateData]);

  const mlaOptions = useMemo(() => {
    if (formData.state === "Bihar") {
      return biharData.mla_constituencies.filter(
        (mla) => !formData.district || mla.district === formData.district,
      );
    }

    return (selectedStateData?.mlas || []).filter(
      (mla) => !formData.district || !mla.district || mla.district === formData.district,
    );
  }, [formData.district, formData.state, selectedStateData]);

  const mpOptions = useMemo(() => {
    if (formData.state === "Bihar") {
      return biharData.mp_constituencies;
    }

    return (selectedStateData?.mps || []).map((mp) => mp.constituency);
  }, [formData.state, selectedStateData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
      ...(name === "state" && {
        district: "",
        mlaConstituency: "",
        mpConstituency: "",
      }),
      ...(name === "district" && { mlaConstituency: "" }),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.fullName.trim()) nextErrors.fullName = "Name is required.";
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) nextErrors.email = "Valid email is required.";
    if (!formData.state) nextErrors.state = "State is required.";
    if (!formData.district) nextErrors.district = "District is required.";
    if (!formData.mlaConstituency) nextErrors.mlaConstituency = "MLA constituency is required.";
    if (!formData.mpConstituency) nextErrors.mpConstituency = "MP constituency is required.";
    if (!formData.streetAddress.trim()) nextErrors.streetAddress = "Address is required.";
    if (!/^\d{6}$/.test(formData.pincode)) nextErrors.pincode = "Enter a valid 6-digit pincode.";
    if (!formData.password.trim()) nextErrors.password = "Password is required.";
    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const verifiedPhone = localStorage.getItem("verifiedPhone");
    if (!verifiedPhone) {
      toast.error("Please verify your phone number first.");
      navigate("/register");
      return;
    }

    const payload = {
      name: formData.fullName,
      email: formData.email,
      phone: verifiedPhone,
      password: formData.password,
      permanentAddress: {
        state: formData.state,
        district: formData.district,
        mlaConstituency: formData.mlaConstituency,
        mpConstituency: formData.mpConstituency,
        pincode: formData.pincode,
        addressLine1: formData.streetAddress,
      },
    };

    try {
      setLoading(true);
      const response = await api.post("/auth/register", payload);

      if (response.data.success) {
        toast.success("Registration successful");
        localStorage.removeItem("verifiedPhone");
        localStorage.removeItem("pendingRegisterEmail");
        navigate("/login");
      } else {
        toast.error(response.data.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(error?.response?.data?.message || "Server error during registration.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Complete your civic profile"
      subtitle="Finish the final step with structured voter details so JanShakti can auto-route issues to the right MLA and MP."
      step={4}
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Basic Info</h2>
            <p className="text-sm text-slate-500">Tell us who is joining JanShakti.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              icon={UserRound}
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              error={errors.fullName}
              placeholder="Enter full name"
            />
            <Field
              icon={Mail}
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              error={errors.email}
              type="email"
              placeholder="Enter email address"
            />
          </div>
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Location</h2>
            <p className="text-sm text-slate-500">Smart mapping helps us prefill your representatives later.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">State</span>
              <div className={`rounded-2xl border bg-white px-4 py-3 ${errors.state ? "border-rose-300" : "border-slate-200"}`}>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full bg-transparent text-sm outline-none"
                >
                  {(availableStates.length ? availableStates : [{ state_name: "Bihar" }]).map((state) => (
                    <option key={state.state_name} value={state.state_name}>
                      {state.state_name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.state && <p className="text-sm text-rose-500">{errors.state}</p>}
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">District</span>
              <div className={`rounded-2xl border bg-white px-4 py-3 ${errors.district ? "border-rose-300" : "border-slate-200"}`}>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  className="w-full bg-transparent text-sm outline-none"
                >
                  <option value="">Select district</option>
                  {districtOptions.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>
              {errors.district && <p className="text-sm text-rose-500">{errors.district}</p>}
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">MLA Constituency</span>
              <div className={`rounded-2xl border bg-white px-4 py-3 ${errors.mlaConstituency ? "border-rose-300" : "border-slate-200"}`}>
                <select
                  name="mlaConstituency"
                  value={formData.mlaConstituency}
                  onChange={handleInputChange}
                  className="w-full bg-transparent text-sm outline-none"
                >
                  <option value="">Select legislative constituency</option>
                  {mlaOptions.map((mla) => (
                    <option
                      key={mla.serial_no || `${mla.constituency}-${mla.name}`}
                      value={mla.constituency}
                    >
                      {mla.constituency}
                    </option>
                  ))}
                </select>
              </div>
              {errors.mlaConstituency && <p className="text-sm text-rose-500">{errors.mlaConstituency}</p>}
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">MP Constituency</span>
              <div className={`rounded-2xl border bg-white px-4 py-3 ${errors.mpConstituency ? "border-rose-300" : "border-slate-200"}`}>
                <select
                  name="mpConstituency"
                  value={formData.mpConstituency}
                  onChange={handleInputChange}
                  className="w-full bg-transparent text-sm outline-none"
                >
                  <option value="">Select Lok Sabha constituency</option>
                  {mpOptions.map((mp) => (
                    <option key={mp} value={mp}>
                      {mp}
                    </option>
                  ))}
                </select>
              </div>
              {errors.mpConstituency && <p className="text-sm text-rose-500">{errors.mpConstituency}</p>}
            </label>
          </div>
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Address</h2>
            <p className="text-sm text-slate-500">Used for local issue verification and voter trust.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              icon={MapPin}
              label="Address"
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleInputChange}
              error={errors.streetAddress}
              placeholder="House number, street, landmark"
            />
            <Field
              icon={MapPinned}
              label="Pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleInputChange}
              error={errors.pincode}
              placeholder="6-digit pincode"
              maxLength="6"
            />
            <Field
              icon={Mail}
              label="Password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
              type="password"
              placeholder="Create password"
            />
            <Field
              icon={Mail}
              label="Confirm Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              error={errors.confirmPassword}
              type="password"
              placeholder="Repeat password"
            />
          </div>
        </section>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-5 py-4 text-sm font-semibold text-white shadow-xl shadow-orange-200/60 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? (
            <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            "Create Account"
          )}
        </button>

        <p className="text-center text-sm text-slate-600">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-orange-600 hover:text-orange-700">
            Login
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default RegisterDetails;
